import styled from 'styled-components';

import InboundReferralSectionPreferences from 'components/notificationPreferences/InboundReferralSectionPreferences';
import { INBOUND_REFERRAL_SECTIONS } from 'components/notificationPreferences/inboundReferralSections';
import { PreferencesFormMeta, PreferencesFormValues } from 'components/notificationPreferences/NotificationPreferences';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown.tsx';
import InputGroup from 'components/shared/form/InputGroup';
import Toggle from 'components/shared/Toggle.tsx';
import { useForm } from 'context/form';
import { referralBuildingTerminology } from 'hooks/useReferralBuildingTerminology';
import AttrValue from 'models/AttrValue';
import Client from 'models/Client';
import Group from 'models/Group.ts';
import { InboundReferralPreference } from 'models/Notifications';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { useInfiniteGroups } from 'services/api/group';
import { BodySmall, H3 } from 'styles/typography';

import { VALUE_NAMES } from '../inboundReferrals/constants';

// The one form key the inbound-referrals row lives under — its sibling keys are the
// care-coordination rows keyed by group-type id, which are always uuids, so this never collides.
export const INBOUND_REFERRALS_FORM_KEY = 'inboundReferrals';

// The Inbound Referrals half of the preferences page (OLIO-3322): one row per (user, client),
// no group-type tabs. Scoping narrows by referral source (display-name strings — the value the
// api stores verbatim) and by specific buildings (group ids, hydrated by the api on read).
// `client` is the client the page's selector addresses, not the acting client: its level of care
// decides which buildings are offered and what they are called.
function InboundReferralPreferencesContainer({
  clientId,
  client,
  disabled: disabledProp = false,
}: {
  clientId: string;
  client: Client;
  disabled?: boolean;
}) {
  const { values, setValue } = useForm<PreferencesFormValues, PreferencesFormMeta>();
  const { groupType, preferencesTitle } = referralBuildingTerminology(client);

  const preference = (values[INBOUND_REFERRALS_FORM_KEY] ?? {}) as InboundReferralPreference;

  // The same option set the referral list's Referral Source filter offers: one row per distinct
  // display name under the client's referral-source attr. The stored scope value IS the display
  // name, so the option's name doubles as its identity.
  const asyncReferralSourceOptions = useInfiniteAttrValues({
    workflowValueName: VALUE_NAMES.REFERRAL_SOURCE,
    distinctDisplayName: true,
    active: true,
    client: clientId,
  });

  // The client's OWN buildings only. Unlike the care-coordination groups picker this does
  // not reach through networks: inbound referrals land at facilities the client owns.
  const asyncGroupOptions = useInfiniteGroups({
    type: groupType,
    sortBy: 'name asc',
    clientId,
    enforceScopedClient: false,
  });

  const referralSources = preference.scopes?.referralSources ?? [];

  return (
    <ToggleAndFormContainer>
      <Banner $active={!!preference.active}>
        <BannerText>
          <BannerTitle>Inbound Referrals Notifications</BannerTitle>
          <BannerSubtitle>
            {preference.active
              ? 'All notifications are on for Inbound Referrals'
              : 'Notifications are disabled for Inbound Referrals'}
          </BannerSubtitle>
        </BannerText>
        <Toggle
          active={!!preference.active}
          disabled={disabledProp}
          onChange={(active) => setValue(`${INBOUND_REFERRALS_FORM_KEY}.active`, active)}
        />
      </Banner>
      <StyledFormContainer>
        <SectionContainer>
          <StyledH3>Notification Criteria</StyledH3>
          <SectionDescription>
            Indicate below if you wish to only receive notifications for referrals that meet a specific set of criteria.
          </SectionDescription>
          <InputGroup>
            <MultiSelectChips<AttrValue>
              title='Referral Source'
              {...asyncReferralSourceOptions}
              disabled={disabledProp || !preference.active}
              getOptionValue={(option) => option.name}
              getOptionLabel={(option) => option.name}
              onChange={(sources) => {
                setValue(
                  `${INBOUND_REFERRALS_FORM_KEY}.scopes.referralSources`,
                  sources.map((source) => source.name)
                );
              }}
              values={referralSources.map((name) => new AttrValue({ name }))}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips<Group>
              title={preferencesTitle}
              {...asyncGroupOptions}
              disabled={disabledProp || !preference.active}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              onChange={(groups) => {
                setValue(`${INBOUND_REFERRALS_FORM_KEY}.scopes.groups`, groups);
              }}
              values={preference.scopes?.groups ?? []}
            />
          </InputGroup>
        </SectionContainer>
        <SelectSection>
          <StyledH3>Select Notifications</StyledH3>
          <SectionDescription>Choose which Inbound Referral events trigger a notification</SectionDescription>
          {INBOUND_REFERRAL_SECTIONS.map((section) => (
            <InboundReferralSectionPreferences
              key={section.category}
              section={section}
              disabled={disabledProp || !preference.active}
            />
          ))}
        </SelectSection>
      </StyledFormContainer>
    </ToggleAndFormContainer>
  );
}

const Banner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.blue100 : theme.colors.black10)};
  background-color: ${({ $active, theme }) => ($active ? theme.colors.blue5 : theme.colors.white)};
  padding: 16px 24px;
  transition: 0.2s;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.div`
  color: ${({ theme }) => theme.colors.black100};
  font-size: 14px;
  font-weight: 500;
`;

const BannerSubtitle = styled(BodySmall)`
  font-size: 12px;
`;

const SectionContainer = styled.div`
  height: fit-content;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.black10};
  background-color: ${(props) => props.theme.colors.white};
  padding: 24px;
  width: 100%;
`;

const SelectSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledH3 = styled(H3)`
  margin-bottom: 12px;
`;

const StyledFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const ToggleAndFormContainer = styled.div`
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionDescription = styled(BodySmall)`
  font-size: 12px;
  margin-bottom: 24px;
`;

export default InboundReferralPreferencesContainer;
